import React, { useState, useEffect, useCallback } from "react"
import {
  Row, Col, Card, CardHeader, CardBody, Spinner, Alert, Input,
} from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { toast } from "react-toastify"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  getAdminDashboardStats,
  getAdminAllSubscriptions,
} from "../../api/requests/subscription-requests"
import { AssignSubscriptionModal } from "../modals/subscription/AssignSubscriptionModal"
import RefundModal from "../modals/subscription/RefundModal"
import EditSubscriptionSettingsModal from "../modals/subscription/EditSubscriptionSettingsModal"
import { formatLicenseDate, isUnlimitedDate } from "../../utils/subscriptionDates"

/* ────────────────── Helpers ────────────────── */

const NAVY = "#221C5D"
const ORANGE = "#E56A22"

const useDebouncedValue = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const formatPrice = (price) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price || 0) + " ₺"

const formatDate = (d) => formatLicenseDate(d)

const STATUS_BADGE = {
  ACTIVE:   { label: "Aktif",           color: "#71C30B" },
  TRIAL:    { label: "Deneme",          color: "#E56A22" },
  PENDING:  { label: "Ödeme Bekliyor",  color: "#E56A22" },
  EXPIRED:  { label: "Süresi Dolmuş",  color: "#B0B7C3" },
  CANCELLED:{ label: "İptal",           color: "#E07A7A" },
  UPGRADED: { label: "Yükseltilmiş",   color: "#221C5D" },
  SCHEDULED:{ label: "Planlandı",       color: "#221C5D" },
}

const StatusBadge = ({ status }) => {
  const cfg = STATUS_BADGE[status] || { label: status, color: "#B0B7C3" }
  return (
    <span className="badge border-0" style={{ borderRadius: 999, padding: ".35rem .6rem", backgroundColor: cfg.color, color: "#fff" }}>
      {cfg.label}
    </span>
  )
}

const TableCard = ({ className = "", children, ...rest }) => (
  <Card className={`rounded-4 border-0 shadow-lg ${className}`} style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }} {...rest}>
    {children}
  </Card>
)

/* ────────────────── PaginationBar ────────────────── */

const PaginationBar = ({ currentPage, totalPages, totalElements, pageSize, onPageChange, onPageSizeChange }) => {
  const getPages = () => {
    const arr = []
    const max = 5
    let start = Math.max(0, currentPage - Math.floor(max / 2))
    let end = Math.min(totalPages - 1, start + max - 1)
    if (end - start + 1 < max) start = Math.max(0, end - max + 1)
    for (let i = start; i <= end; i++) arr.push(i)
    return arr
  }
  if (!totalElements) return null
  return (
    <div className="d-flex justify-content-between align-items-center mt-3 pagination-bar">
      <div className="text-muted" style={{ fontSize: ".9rem" }}>
        {totalElements} kayıttan{" "}
        {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)}{" "}
        arası gösteriliyor
      </div>
      <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
        <div className="d-flex align-items-center gap-2 page-size-wrap">
          <span className="text-muted" style={{ fontSize: ".9rem" }}>Sayfa başına</span>
          <select
            className="form-select form-select-sm rounded-pill page-size-select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
          >
            {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <nav>
          <ul className="pagination pagination-sm mb-0 corporate-pagination">
            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0}>
                <i className="bx bx-chevron-left" />
              </button>
            </li>
            {getPages().map(p => (
              <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                <button className="page-link" onClick={() => onPageChange(p)}>{p + 1}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage >= totalPages - 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
                <i className="bx bx-chevron-right" />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  )
}

/* ────────────────── Main Component ────────────────── */

const SubscriptionDashboard = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Stats
  const [stats, setStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(true)

  // Table
  const [subscriptions, setSubscriptions] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(() => parseInt(searchParams.get("page")) || 0)
  const [pageSize, setPageSize] = useState(() => parseInt(searchParams.get("size")) || 25)

  // Filters
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "")
  const [planFilter, setPlanFilter] = useState(() => searchParams.get("plan") || "")
  const [domainFilter, setDomainFilter] = useState(() => searchParams.get("domain") || "")
  const [expiringFilter, setExpiringFilter] = useState(() => searchParams.get("expiring") || "")
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "")
  const debouncedSearch = useDebouncedValue(searchTerm, 500)

  // Sorting
  const [sortBy, setSortBy] = useState(() => searchParams.get("sortBy") || "")
  const [sortDir, setSortDir] = useState(() => searchParams.get("sortDir") || "asc")

  // Modals
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [refundModalOpen, setRefundModalOpen] = useState(false)
  const [selectedRefundSub, setSelectedRefundSub] = useState(null)
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const [selectedSettingsSub, setSelectedSettingsSub] = useState(null)

  document.title = "Abonelik Yönetimi | Tahsilat360"

  /* ────── URL Params Sync ────── */

  const updateUrlParams = useCallback((overrides = {}) => {
    const state = {
      page: currentPage,
      size: pageSize,
      search: searchTerm,
      status: statusFilter,
      plan: planFilter,
      domain: domainFilter,
      expiring: expiringFilter,
      sortBy,
      sortDir,
      ...overrides,
    }
    const params = new URLSearchParams()
    Object.entries(state).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined
        && !(key === "page" && value === 0)
        && !(key === "size" && value === 25)
        && !(key === "sortDir" && value === "asc" && !state.sortBy)) {
        params.set(key, String(value))
      }
    })
    setSearchParams(params, { replace: true })
  }, [currentPage, pageSize, searchTerm, statusFilter, planFilter, domainFilter, expiringFilter, sortBy, sortDir, setSearchParams])

  const styles = `
    .sub-dashboard-modern .table thead tr { background-color: #f9f9f9; }
    .sub-dashboard-modern .table th { border: 0; color: #6c757d; font-weight: 600; font-size: .85rem; }
    .sub-dashboard-modern .table td { vertical-align: middle; font-size: .85rem; }
    .sub-dashboard-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .sub-dashboard-modern .btn-rounded { border-radius: 999px; }
    .sub-dashboard-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .sub-dashboard-modern .btn-navy:hover { filter: brightness(1.03); }
    .sub-dashboard-modern .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
    .sub-dashboard-modern .btn-orange:hover { filter: brightness(1.03); }
    .sub-dashboard-modern .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    .sub-dashboard-modern .sortable-th { cursor: pointer; user-select: none; white-space: nowrap; }
    .sub-dashboard-modern .sortable-th:hover { color: ${NAVY}; }
    .sub-dashboard-modern .pill-input, .sub-dashboard-modern .pill-select {
      border-radius: 999px !important; padding: .45rem .9rem;
    }
    .sub-dashboard-modern .corporate-pagination .page-link { color: ${NAVY}; border-color: ${ORANGE}; }
    .sub-dashboard-modern .corporate-pagination .page-item.active .page-link {
      background-color: ${NAVY}; border-color: ${NAVY}; color: #fff;
    }
    .sub-dashboard-modern .corporate-pagination .page-link:hover { border-color: ${NAVY}; color: ${NAVY}; }
    .sub-dashboard-modern .header-filters {
      display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; justify-content: flex-end;
    }
    .sub-dashboard-modern .header-filter-select {
      min-width: 140px; flex: 1 1 140px; max-width: 200px;
    }
    .sub-dashboard-modern .search-input {
      min-width: 160px; flex: 2 1 180px; max-width: 260px;
    }
    .sub-dashboard-modern .page-size-wrap { white-space: nowrap; }
    .sub-dashboard-modern .page-size-select { min-width: 70px; padding-right: 1.75rem; }
    .sub-dashboard-modern .pagination-bar { flex-wrap: wrap; gap: .5rem; }
    .sub-dashboard-modern .stat-row {
      display: flex; gap: .75rem; overflow-x: auto; padding-bottom: .5rem;
    }
    .sub-dashboard-modern .stat-chip {
      display: flex; align-items: center; gap: .6rem;
      background: #fff; border-radius: 12px; padding: .65rem 1rem;
      box-shadow: 0 2px 8px rgba(0,0,0,.06);
      white-space: nowrap; min-width: 0; flex-shrink: 0;
    }
    .sub-dashboard-modern .stat-icon-box {
      width: 36px; height: 36px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }
    @media (max-width: 1280px) {
      .sub-dashboard-modern .header-filter-select { max-width: 180px; }
      .sub-dashboard-modern .search-input { max-width: 200px; }
    }
    @media (max-width: 992px) {
      .sub-dashboard-modern .search-input { max-width: 100%; flex: 1 1 160px; }
      .sub-dashboard-modern .pagination-bar { justify-content: flex-start !important; }
    }
    @media (max-width: 576px) {
      .sub-dashboard-modern .table { font-size: 12px; }
      .sub-dashboard-modern .table td, .sub-dashboard-modern .table th { padding: .5rem; }
      .sub-dashboard-modern .header-filter-select { max-width: 100%; flex: 1 1 100%; }
      .sub-dashboard-modern .search-input { max-width: 100%; }
    }
  `

  /* ────── Data fetching ────── */

  const loadStats = async () => {
    try {
      setStatsLoading(true)
      const res = await getAdminDashboardStats()
      setStats(res.data)
    } catch (err) {
      console.error("Stats error:", err)
    } finally {
      setStatsLoading(false)
    }
  }

  const fetchSubscriptions = async (page = 0, size = 25, search = "", status = "", plan = "", domain = "", expiring = "", first = false, sort = sortBy, dir = sortDir) => {
    try {
      setError(null)
      if (first) setLoading(true); else setRefreshing(true)
      const res = await getAdminAllSubscriptions({
        status, search, page, size, plan, domain, expiring, groupBy: "company",
        sortBy: sort || undefined, sortDir: sort ? dir : undefined,
      })
      setSubscriptions(res.data?.content || [])
      setTotalElements(res.data?.totalElements || 0)
      setTotalPages(res.data?.totalPages || 0)
      setCurrentPage(res.data?.page ?? page)
      setPageSize(res.data?.size ?? size)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // İlk yükleme (URL parametrelerinden state okunuyor)
  useEffect(() => {
    loadStats()
    fetchSubscriptions(currentPage, pageSize, searchTerm, statusFilter, planFilter, domainFilter, expiringFilter, true, sortBy, sortDir)
  }, []) // eslint-disable-line

  // Debounced search değişince
  useEffect(() => {
    fetchSubscriptions(0, pageSize, debouncedSearch, statusFilter, planFilter, domainFilter, expiringFilter, false, sortBy, sortDir)
    updateUrlParams({ search: debouncedSearch, page: 0 })
  }, [debouncedSearch]) // eslint-disable-line

  /* ────── Filter handlers ────── */

  const handleStatusChange = (val) => {
    setStatusFilter(val)
    setCurrentPage(0)
    fetchSubscriptions(0, pageSize, debouncedSearch, val, planFilter, domainFilter, expiringFilter, false, sortBy, sortDir)
    updateUrlParams({ status: val, page: 0 })
  }
  const handlePlanChange = (val) => {
    setPlanFilter(val)
    setCurrentPage(0)
    fetchSubscriptions(0, pageSize, debouncedSearch, statusFilter, val, domainFilter, expiringFilter, false, sortBy, sortDir)
    updateUrlParams({ plan: val, page: 0 })
  }
  const handleDomainChange = (val) => {
    setDomainFilter(val)
    setCurrentPage(0)
    fetchSubscriptions(0, pageSize, debouncedSearch, statusFilter, planFilter, val, expiringFilter, false, sortBy, sortDir)
    updateUrlParams({ domain: val, page: 0 })
  }
  const handleExpiringChange = (val) => {
    setExpiringFilter(val)
    setCurrentPage(0)
    fetchSubscriptions(0, pageSize, debouncedSearch, statusFilter, planFilter, domainFilter, val, false, sortBy, sortDir)
    updateUrlParams({ expiring: val, page: 0 })
  }
  const handlePageChange = (p) => {
    if (p >= 0 && p < totalPages) {
      setCurrentPage(p)
      fetchSubscriptions(p, pageSize, debouncedSearch, statusFilter, planFilter, domainFilter, expiringFilter, false, sortBy, sortDir)
      updateUrlParams({ page: p })
    }
  }
  const handlePageSizeChange = (sz) => {
    setPageSize(sz)
    setCurrentPage(0)
    fetchSubscriptions(0, sz, debouncedSearch, statusFilter, planFilter, domainFilter, expiringFilter, false, sortBy, sortDir)
    updateUrlParams({ size: sz, page: 0 })
  }
  const handleSort = (field) => {
    let newDir = "asc"
    if (sortBy === field) {
      newDir = sortDir === "asc" ? "desc" : "asc"
    }
    setSortBy(field)
    setSortDir(newDir)
    setCurrentPage(0)
    fetchSubscriptions(0, pageSize, debouncedSearch, statusFilter, planFilter, domainFilter, expiringFilter, false, field, newDir)
    updateUrlParams({ sortBy: field, sortDir: newDir, page: 0 })
  }
  const refreshCurrent = () => {
    loadStats()
    fetchSubscriptions(currentPage, pageSize, debouncedSearch, statusFilter, planFilter, domainFilter, expiringFilter, false, sortBy, sortDir)
  }

  /* ────── Modals ────── */

  const openAssignModal = (sub) => {
    setSelectedCompany({ id: sub.companyId, companyName: sub.companyName })
    setAssignModalOpen(true)
  }
  const onAssignSuccess = () => {
    setAssignModalOpen(false)
    setSelectedCompany(null)
    refreshCurrent()
    toast.success("Plan değişikliği başarılı")
  }
  const openRefundModal = (sub) => { setSelectedRefundSub(sub); setRefundModalOpen(true) }
  const onRefundSuccess = () => { setRefundModalOpen(false); setSelectedRefundSub(null); refreshCurrent() }
  const openSettingsModal = (sub) => { setSelectedSettingsSub(sub); setSettingsModalOpen(true) }
  const onSettingsSuccess = () => { setSettingsModalOpen(false); setSelectedSettingsSub(null); refreshCurrent() }

  /* ────── Sort icon helper ────── */

  const SortIcon = ({ field }) => (
    <i
      className={`bx ms-1 ${sortBy === field ? (sortDir === "asc" ? "bx-sort-up" : "bx-sort-down") : "bx-sort"}`}
      style={{ opacity: sortBy === field ? 1 : 0.35, fontSize: ".75rem" }}
    />
  )

  /* ────── Plan isimleri (stats'tan veya subscriptions'tan çıkar) ────── */

  const uniquePlanNames = [...new Set(subscriptions.map(s => s.planName).filter(Boolean))]

  /* ────── Stat chips ────── */

  const statChips = stats ? [
    { label: "Toplam Firma",        value: stats.totalCompanies,          icon: "bx-buildings",    color: "#221C5D", bg: "rgba(34,28,93,.1)" },
    { label: "Aktif",               value: stats.activeSubscriptions,     icon: "bx-check-circle", color: "#71C30B", bg: "rgba(113,195,11,.1)" },
    { label: "Deneme",              value: stats.trialSubscriptions,      icon: "bx-time-five",    color: "#E56A22", bg: "rgba(229,106,34,.1)" },
    { label: "Süresi Dolmuş",      value: stats.expiredSubscriptions,    icon: "bx-x-circle",     color: "#B0B7C3", bg: "rgba(176,183,195,.15)" },
    { label: "İptal",               value: stats.cancelledSubscriptions,  icon: "bx-block",        color: "#dc3545", bg: "rgba(220,53,69,.1)" },
    { label: "7 Gün İçinde",        value: stats.expiringIn7Days,         icon: "bx-error",        color: "#E56A22", bg: "rgba(229,106,34,.1)" },
    { label: "Toplam Gelir",        value: formatPrice(stats.totalRevenue), icon: "bx-wallet",     color: "#71C30B", bg: "rgba(113,195,11,.1)" },
  ] : []

  /* ────────────────── RENDER ────────────────── */

  return (
    <div className="page-content sub-dashboard-modern">
      <style>{styles}</style>
      <div className="container-fluid">

        {/* ── Stat Chips (tek satır, yatay kaydırılabilir) ── */}
        {statsLoading ? (
          <div className="text-center p-3"><Spinner size="sm" color="primary" /></div>
        ) : stats && (
          <div className="stat-row mb-4">
            {statChips.map((c, i) => (
              <div key={i} className="stat-chip">
                <div className="stat-icon-box" style={{ backgroundColor: c.bg }}>
                  <i className={`bx ${c.icon}`} style={{ fontSize: 18, color: c.color }} />
                </div>
                <div>
                  <div className="fw-bold" style={{ color: c.color, fontSize: ".95rem" }}>{c.value}</div>
                  <div className="text-muted" style={{ fontSize: ".72rem" }}>{c.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Table Card ── */}
        <Row>
          <Col style={{ minWidth: 0 }}>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                {/* DESKTOP header */}
                <div className="d-none d-md-block">
                  <div className="mb-3">
                    <h5 className="mb-1 fw-bold text-dark">Abonelik Yönetimi</h5>
                    <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                      {totalElements > 0 ? `Toplam ${totalElements} firma` : "Firma bulunamadı"}
                    </p>
                  </div>

                  <div className="header-filters">
                    <select
                      className="form-select pill-select header-filter-select"
                      value={statusFilter}
                      onChange={(e) => handleStatusChange(e.target.value)}
                    >
                      <option value="">Tüm Durumlar</option>
                      <option value="ACTIVE">Aktif</option>
                      <option value="TRIAL">Deneme</option>
                      <option value="PENDING">Ödeme Bekliyor</option>
                      <option value="EXPIRED">Süresi Dolmuş</option>
                      <option value="CANCELLED">İptal Edilmiş</option>
                      <option value="UPGRADED">Yükseltilmiş</option>
                    </select>

                    <select
                      className="form-select pill-select header-filter-select"
                      value={planFilter}
                      onChange={(e) => handlePlanChange(e.target.value)}
                    >
                      <option value="">Tüm Planlar</option>
                      {uniquePlanNames.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>

                    <select
                      className="form-select pill-select header-filter-select"
                      value={domainFilter}
                      onChange={(e) => handleDomainChange(e.target.value)}
                    >
                      <option value="">Domain Filtresi</option>
                      <option value="custom">Özel Domain Var</option>
                      <option value="none">Özel Domain Yok</option>
                    </select>

                    <select
                      className="form-select pill-select header-filter-select"
                      value={expiringFilter}
                      onChange={(e) => handleExpiringChange(e.target.value)}
                    >
                      <option value="">Süre Filtresi</option>
                      <option value="7">7 gün içinde dolacak</option>
                      <option value="30">30 gün içinde dolacak</option>
                      <option value="90">90 gün içinde dolacak</option>
                    </select>

                    <Input
                      type="search"
                      placeholder="Firma adı, kodu ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input pill-input"
                    />
                  </div>
                </div>

                {/* MOBILE header */}
                <div className="d-md-none">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0 fw-semibold">Abonelik Yönetimi</h5>
                  </div>
                  <p className="mb-2 text-muted" style={{ fontSize: ".9rem" }}>
                    {totalElements > 0 ? `Toplam ${totalElements} firma` : "Firma bulunamadı"}
                  </p>
                  <div className="d-flex flex-column gap-2">
                    <Input
                      type="search"
                      placeholder="Firma adı, kodu ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pill-input"
                    />
                    <div className="d-flex gap-2 flex-wrap">
                      <select className="form-select pill-select" value={statusFilter} onChange={(e) => handleStatusChange(e.target.value)}>
                        <option value="">Tüm Durumlar</option>
                        <option value="ACTIVE">Aktif</option>
                        <option value="TRIAL">Deneme</option>
                        <option value="PENDING">Ödeme Bekliyor</option>
                        <option value="EXPIRED">Süresi Dolmuş</option>
                        <option value="CANCELLED">İptal</option>
                      </select>
                      <select className="form-select pill-select" value={planFilter} onChange={(e) => handlePlanChange(e.target.value)}>
                        <option value="">Tüm Planlar</option>
                        {uniquePlanNames.map(name => <option key={name} value={name}>{name}</option>)}
                      </select>
                      <select className="form-select pill-select" value={expiringFilter} onChange={(e) => handleExpiringChange(e.target.value)}>
                        <option value="">Süre Filtresi</option>
                        <option value="7">7 gün</option>
                        <option value="30">30 gün</option>
                        <option value="90">90 gün</option>
                      </select>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardBody className="pt-2">
                {error && (
                  <Alert color="danger" className="mb-3 rounded-4">
                    <i className="bx bx-error-circle me-2" />
                    {error}
                    <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={refreshCurrent}>
                      Tekrar Dene
                    </button>
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center p-5">
                    <Spinner size="lg" color="primary" />
                    <h6 className="mt-3 text-muted">Abonelikler yükleniyor...</h6>
                  </div>
                ) : (
                  <>
                    <div className={`d-flex justify-content-between align-items-center mb-2 ${refreshing ? "opacity-50" : ""}`}>
                      <div className="text-muted" style={{ fontSize: ".9rem" }}>
                        {totalElements > 0 && (
                          <>Toplam <strong>{totalElements}</strong> kayıt - Sayfa <strong>{currentPage + 1}</strong> / {totalPages}</>
                        )}
                      </div>
                      <button className="btn btn-light btn-sm btn-rounded" onClick={refreshCurrent} title="Yenile">
                        <i className={`bx bx-refresh ${refreshing ? "bx-spin" : ""}`} />
                      </button>
                    </div>

                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th className="sortable-th" onClick={() => handleSort("companyName")}>
                              Firma Adı<SortIcon field="companyName" />
                            </Th>
                            <Th className="d-none d-lg-table-cell">Firma Kodu</Th>
                            <Th className="sortable-th" onClick={() => handleSort("planName")}>
                              Plan<SortIcon field="planName" />
                            </Th>
                            <Th className="sortable-th" onClick={() => handleSort("status")}>
                              Durum<SortIcon field="status" />
                            </Th>
                            <Th className="d-none d-md-table-cell sortable-th" onClick={() => handleSort("startDate")}>
                              Başlangıç<SortIcon field="startDate" />
                            </Th>
                            <Th className="d-none d-md-table-cell sortable-th" onClick={() => handleSort("endDate")}>
                              Bitiş<SortIcon field="endDate" />
                            </Th>
                            <Th className="d-none d-lg-table-cell sortable-th" onClick={() => handleSort("daysRemaining")}>
                              Kalan Gün<SortIcon field="daysRemaining" />
                            </Th>
                            <Th className="d-none d-xl-table-cell">Cari</Th>
                            <Th className="d-none d-xl-table-cell">Alt / Cari Kullanıcı</Th>
                            <Th className="d-none d-md-table-cell sortable-th" onClick={() => handleSort("paidAmount")}>
                              Tutar<SortIcon field="paidAmount" />
                            </Th>
                            <Th className="text-end">İşlemler</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {subscriptions.length ? subscriptions.map((sub, idx) => (
                            <Tr key={sub.id || idx} className="border-bottom">
                              <Td className="fw-semibold text-dark">{sub.companyName}</Td>
                              <Td className="d-none d-lg-table-cell text-muted">{sub.companyCode || "-"}</Td>
                              <Td>{sub.planName}</Td>
                              <Td><StatusBadge status={sub.status} /></Td>
                              <Td className="d-none d-md-table-cell">{formatDate(sub.startDate)}</Td>
                              <Td className="d-none d-md-table-cell">{formatDate(sub.endDate)}</Td>
                              <Td className="d-none d-lg-table-cell">
                                <span style={{
                                  fontWeight: 600,
                                  color: sub.daysRemaining <= 7 ? "#dc3545" : sub.daysRemaining <= 30 ? "#E56A22" : "#71C30B",
                                }}>
                                  {isUnlimitedDate(sub.endDate) ? "Süresiz" : (sub.daysRemaining ?? "-")}
                                </span>
                              </Td>
                              <Td className="d-none d-xl-table-cell">
                                {sub.currentAccounts}/{sub.maxAccounts || "∞"}
                              </Td>
                              <Td className="d-none d-xl-table-cell">
                                {sub.currentUsers}/{sub.maxUsers || "∞"}
                                <div className="text-muted" style={{ fontSize: ".72rem" }}>
                                  cari: {sub.currentAccountUsers ?? 0}/{sub.maxAccountUsers || "∞"}
                                </div>
                              </Td>
                              <Td className="d-none d-md-table-cell">{formatPrice(sub.paidAmount)}</Td>
                              <Td className="text-end">
                                <div className="d-flex gap-1 justify-content-end flex-wrap">
                                  <button
                                    className="btn btn-sm action-btn btn-orange"
                                    title="Abonelik Detay"
                                    onClick={() => navigate(`/admin/subscription-detail/${sub.companyId}`)}
                                  >
                                    <i className="bx bx-show" />
                                  </button>
                                  <button
                                    className="btn btn-sm action-btn btn-navy"
                                    title="Plan Değiştir"
                                    onClick={() => openAssignModal(sub)}
                                  >
                                    <i className="bx bx-transfer" />
                                  </button>
                                  <button
                                    className="btn btn-sm action-btn"
                                    style={{ background: "#6c757d", borderColor: "#6c757d", color: "#fff" }}
                                    title="Abonelik Ayarları"
                                    onClick={() => openSettingsModal(sub)}
                                  >
                                    <i className="bx bx-cog" />
                                  </button>
                                  {(() => {
                                    const canRefund = sub.paidAmount > 0 && !sub.refunded;
                                    return (
                                      <button
                                        className="btn btn-sm btn-outline-warning action-btn"
                                        title={canRefund ? "İade İşaretle" : sub.refunded ? "İade edilmiş" : "Ödeme tutarı yok"}
                                        onClick={() => canRefund && openRefundModal(sub)}
                                        disabled={!canRefund}
                                        style={{
                                          ...(!canRefund ? { opacity: 0.4, filter: "blur(0.5px)", cursor: "not-allowed" } : {}),
                                        }}
                                      >
                                        <i className="bx bx-money" />
                                      </button>
                                    );
                                  })()}
                                </div>
                              </Td>
                            </Tr>
                          )) : (
                            <Tr>
                              <Td colSpan="11" className="text-center py-4">
                                <div className="text-muted">
                                  <i className="bx bx-package display-4 d-block mb-2" />
                                  Abonelik bulunamadı
                                  {searchTerm && <div className="mt-2"><small>Arama teriminizi değiştirmeyi deneyin</small></div>}
                                </div>
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </div>

                    <PaginationBar
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalElements={totalElements}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </>
                )}
              </CardBody>

              {/* Modals */}
              <AssignSubscriptionModal
                isOpen={assignModalOpen}
                toggle={() => { setAssignModalOpen(false); setSelectedCompany(null) }}
                company={selectedCompany}
                onSuccess={onAssignSuccess}
              />
              <RefundModal
                isOpen={refundModalOpen}
                toggle={() => { setRefundModalOpen(false); setSelectedRefundSub(null) }}
                subscription={selectedRefundSub}
                onConfirm={onRefundSuccess}
              />
              <EditSubscriptionSettingsModal
                isOpen={settingsModalOpen}
                toggle={() => { setSettingsModalOpen(false); setSelectedSettingsSub(null) }}
                subscription={selectedSettingsSub}
                onSuccess={onSettingsSuccess}
              />
            </TableCard>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default SubscriptionDashboard
